import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, Box, IconButton, Button, Modal, Autocomplete, TextField, Tabs, Tab, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import TransferBondLayout from '../transferbond/tbondlayout';
import { useUser } from '../../services/userContext';
import RequestsGrid from './postingsubcomponents/postingOpenRequests'; 
import QuoteGrids from './postingsubcomponents/postingQuotes'; 
import TerritoriesAndDefaultQuotesModal from './postingsubcomponents/territoriesAndDefaultQuotes';
import AcceptedQuotes from './postingsubcomponents/acceptedQuotes'; // Import AcceptedQuotes component
import { toast } from 'react-toastify';


const PostingLandingPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [refreshToggle, setRefreshToggle] = useState(false); 
  const [activeTab, setActiveTab] = useState(0); 
  const [isTerritoriesModalOpen, setTerritoriesModalOpen] = useState(false);
  const [canViewRequestingAgency, setCanViewRequestingAgency] = useState(false); 

  useEffect(() => {
    // Scrolls the window to the top when the component is mounted
    window.scrollTo(0, 0);
  }, []);

  // Get out of here if no agents can post (shouldnt be able to get here in first place but just in case.)
  useEffect(() => {
    const agenciesWithCanPost = user?.info?.agencyDetails?.filter((agency) => agency.canPost === true);
    
    if (agenciesWithCanPost?.length === 0) {
      toast.error('You do not have any agencies that can post. Please contact Bankers if you believe this is an error.');
      navigate('/TransferBond'); // Redirect to transfer bond landing if no agencies can post
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchPermissions = async () => {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;
  
      try {
        // Fetch user details to get SubAgentID and AgencyID
        const userDetailsResponse = await fetch(`${baseURL}user/info`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
  
        if (!userDetailsResponse.ok) {
          throw new Error(`Error fetching user details: ${userDetailsResponse.statusText}`);
        }
  
        const userDetails = await userDetailsResponse.json();
        const { subAgentId, agencyId } = userDetails;
  
        // Fetch permissions based on SubAgentID and AgencyID
        const permissionsResponse = await fetch(
          `${baseURL}User/${agencyId}/permissions/${subAgentId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
  
        if (!permissionsResponse.ok) {
          throw new Error(`Error fetching user permissions: ${permissionsResponse.statusText}`);
        }
  
        const permissions = await permissionsResponse.json();
        console.log('Fetched Permissions:', permissions);
  
        // Check if PermissionTypeID 10 is available
        const hasPermission = permissions.some(
          (perm) => perm.portalPermissionTypeID === 10 && perm.permit === true
        );
  
        setCanViewRequestingAgency(hasPermission);
        console.log('Can View Requesting Agency:', hasPermission);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };
  
    fetchPermissions();
  }, []);

  useEffect(() => {
    const storedAgency = localStorage.getItem('selectedAgency');
    if (storedAgency) {
      setSelectedAgency(JSON.parse(storedAgency));
    }

    const intervalId = setInterval(() => {
      setRefreshToggle((prev) => !prev); 
    }, 10000);

    return () => clearInterval(intervalId); 
  }, []);

  const handleViewDetails = (id) => {
    navigate(`/view-quote-details?ID=${id}`, { state: { canViewRequestingAgency } });
  };

  const handleBack = () => {
    navigate('/transferbond');
  };

  const handleAgencySelect = (agency) => {
    setSelectedAgency(agency);
    localStorage.setItem('selectedAgency', JSON.stringify(agency));
    setModalOpen(false);
  };

  const handleManualRefresh = () => {
    setRefreshToggle((prev) => !prev); 
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue); 
  };

  const handleDefaultQuotes = () => {
    setTerritoriesModalOpen(true); 
  };

  const agencyOptions = user?.info?.agencyDetails
  ?.filter((agency) => agency.canPost === true) // Filter agencies with CanPost
  .map((agency) => ({
    label: `${agency.dbaAgencyName} (${agency.agencyId})`,
    id: agency.agencyId,
  })) || [];

  return (
    <TransferBondLayout>
      <Container maxWidth="lg" sx={{ paddingTop: 4 }}>
      <Box sx={{ padding: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom sx={{ ml: 1 }}>
            Posting Home Page
          </Typography>
        </Box>

        <Paper sx={{ padding: 3, mb: 4, borderRadius: 1, backgroundColor: '#f9f9f9', boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)', border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            You are posting as: <strong>{selectedAgency?.label || 'Select Agency'}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
              This screen allows you to view all your current transfer bond postings. You can track the status, give quotes, and manage your listings.
            </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user?.info?.agencyDetails?.filter((agency) => agency.canPost === true).length > 1 && (
              <Button variant="contained" color="secondary" onClick={() => setModalOpen(true)}>
                Change Agency
              </Button>
            )}
            <Button variant="contained" color="primary" onClick={handleDefaultQuotes}>
              Territories & Default Quotes
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ mb: 4, backgroundColor: '#f5f5f5' }}>
          <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="fullWidth"  sx={{
              '& .Mui-selected': {
                backgroundColor: '#f9f9f9',
                fontWeight: 'bold',
              },
            }}>
            <Tab label="Available to Quote" />
            <Tab label={`In-Progress Transfers`} />
            <Tab label="Quoted and Declined Requests" />
          </Tabs>

          {activeTab === 0 && selectedAgency && (
            <RequestsGrid selectedAgency={selectedAgency} refreshToggle={refreshToggle} refreshHandler={handleManualRefresh} canViewRequestingAgency={canViewRequestingAgency}/>

          )}

          {activeTab === 1 && selectedAgency && (
               <AcceptedQuotes
               selectedAgency={selectedAgency}
               refreshToggle={refreshToggle}
               handleViewDetails={handleViewDetails}
               canViewRequestingAgency={canViewRequestingAgency}
             />
          )}

          {activeTab === 2 && selectedAgency && (
            <QuoteGrids
              selectedAgency={selectedAgency}
              handleViewDetails={handleViewDetails}
              refreshToggle={refreshToggle}
              refreshHandler={handleManualRefresh}
              canViewRequestingAgency={canViewRequestingAgency}
            />
          )}
        </Paper>

        <Modal open={isModalOpen} onClose={() => null}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select Agency
            </Typography>
            <Autocomplete options={agencyOptions} getOptionLabel={(option) => option.label} onChange={(_, newValue) => handleAgencySelect(newValue)} renderInput={(params) => <TextField {...params} label="Agency" fullWidth />} />
          </Box>
        </Modal>

        <TerritoriesAndDefaultQuotesModal open={isTerritoriesModalOpen} onClose={() => setTerritoriesModalOpen(false)} selectedAgency={selectedAgency} refreshHandler={handleManualRefresh} />
      </Box>
      </Container>
    </TransferBondLayout>
  );
};

export default PostingLandingPage;
